import { type FC, useState } from "react"

import styles from "./KnownIssueDaySummary.module.scss"
import {
	KnownIssueDaySummaryFragment,
	toKnownIssueDaySummary,
} from "./day_summary"

import { FragmentType } from "@/souhl_admin/gql/__generated__"

type KnownIssueDaySummaryProps = {
	daySummaries: FragmentType<typeof KnownIssueDaySummaryFragment>[]
	/** コンパクト表示（一覧テーブル用） */
	compact?: boolean
}

/**
 * ログカウントに応じたヒートマップの色レベルを取得
 */
const getHeatLevel = (count: number): string => {
	if (count === 0) return styles.heatLevel0
	if (count <= 5) return styles.heatLevel1
	if (count <= 20) return styles.heatLevel2
	if (count <= 50) return styles.heatLevel3
	return styles.heatLevel4
}

/**
 * 日付文字列からMM/DD形式に変換
 */
const formatDateShort = (dateStr: string): string => {
	const parts = dateStr.split("-")
	if (parts.length === 3) {
		return `${parseInt(parts[1])}/${parseInt(parts[2])}`
	}
	return dateStr
}

const KnownIssueDaySummary: FC<KnownIssueDaySummaryProps> = ({
	daySummaries: daySummariesFragment,
	compact = false,
}) => {
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

	const daySummaries = daySummariesFragment.map(toKnownIssueDaySummary)

	if (daySummaries.length === 0) {
		return <span className={styles.noData}>-</span>
	}

	return (
		<div className={`${styles.container} ${compact ? styles.compact : ""}`}>
			<div className={styles.summaryGrid}>
				{daySummaries.map((summary, index) => (
					<div
						key={summary.date}
						className={styles.dayWrapper}
						onMouseEnter={() => setHoveredIndex(index)}
						onMouseLeave={() => setHoveredIndex(null)}
					>
						<div
							className={`${styles.dayCell} ${getHeatLevel(summary.matchLogCount)}`}
							title={`${summary.date}: ${summary.matchLogCount}件`}
						>
							<span className={styles.count}>{summary.matchLogCount}</span>
						</div>
						{!compact && (
							<span className={styles.dateLabel}>
								{formatDateShort(summary.date)}
							</span>
						)}

						{/* ツールチップ */}
						{hoveredIndex === index && summary.matchLogCount > 0 && (
							<div className={styles.tooltip}>
								<div className={styles.tooltipHeader}>
									<strong>{summary.date}</strong>
									<span>{summary.matchLogCount}件</span>
								</div>
								{summary.sampleReqId && (
									<div className={styles.tooltipRow}>
										<span className={styles.tooltipLabel}>Sample ReqID:</span>
										<code className={styles.tooltipValue}>
											{summary.sampleReqId}
										</code>
									</div>
								)}
								{summary.sampleMessage && (
									<div className={styles.tooltipRow}>
										<span className={styles.tooltipLabel}>Message:</span>
										<span className={styles.tooltipValue}>
											{summary.sampleMessage.length > 80
												? `${summary.sampleMessage.slice(0, 80)}...`
												: summary.sampleMessage}
										</span>
									</div>
								)}
							</div>
						)}
					</div>
				))}
			</div>
		</div>
	)
}

export default KnownIssueDaySummary
